import { throttle } from 'lodash-es';
import { useEffect, useState } from 'react';

const directionToEdgesMap = {
  horizontal: {
    start: 'left',
    end: 'right',
  },
  vertical: {
    start: 'top',
    end: 'bottom',
  },
};

const scrollDimensionProps = {
  horizontal: {
    scrollPosition: 'scrollLeft',
    totalScrollSize: 'scrollWidth',
    viewportSize: 'clientWidth',
  },
  vertical: {
    scrollPosition: 'scrollTop',
    totalScrollSize: 'scrollHeight',
    viewportSize: 'clientHeight',
  },
};

/**
 * Hook that detects when an element is scrolled to its edges
 *
 * @param {React.RefObject} containerRef - Reference to the scrollable container element
 * @param {Object} options - Configuration options
 * @param {string} [options.direction] - Scroll direction to detect: 'horizontal'|'vertical'
 * @param {React.RefObject} [options.resizableContainerRef] - Reference to a resizable container elm
 * @returns {Object} Indicating if the container is scrolled to each edge
 *                   For horizontal: { left: boolean, right: boolean }
 *                   For vertical: { top: boolean, bottom: boolean }
 */
function useScrollDetection(
  containerRef,
  { direction = 'horizontal', resizableContainerRef } = {}
) {
  const [scrollState, setScrollState] = useState({
    [directionToEdgesMap[direction].start]: false,
    [directionToEdgesMap[direction].end]: false,
  });

  useEffect(() => {
    const containerElement = containerRef?.current;

    const checkScrollState = throttle(() => {
      if (!containerElement) return;

      const { scrollPosition, totalScrollSize, viewportSize } = scrollDimensionProps[direction];
      const {
        [scrollPosition]: scrollStart,
        [totalScrollSize]: scrollSize,
        [viewportSize]: clientSize,
      } = containerElement;

      setScrollState({
        [directionToEdgesMap[direction].start]: scrollStart > 0,
        [directionToEdgesMap[direction].end]: scrollStart + clientSize < scrollSize,
      });
    }, 100);

    let resizeObserver;
    if (resizableContainerRef?.current) {
      const resizableContainer = resizableContainerRef.current;

      resizeObserver = new ResizeObserver(() => {
        checkScrollState();
      });

      resizeObserver.observe(resizableContainer);
    } else {
      // Initial check
      checkScrollState();
    }

    containerElement?.addEventListener('scroll', checkScrollState);

    return () => {
      containerElement?.removeEventListener('scroll', checkScrollState);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [containerRef, resizableContainerRef, direction]);

  return scrollState;
}

export default useScrollDetection;
